"use client";

import { memo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { IconCalendar, IconUsers } from "@tabler/icons-react";
import FormInputField from "@/components/custom-form/FormInputField";
import FormDateRangeField from "@/components/custom-form/FormDateRangeField";
import FormTravelerField from "@/components/custom-form/FormTravelerField";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { BookForMeFormData } from "../book-for-me.type";
import { StepHeader } from "./BookForMeComponents";
import BookForMeDestinationField from "./BookForMeDestinationField";
import BookForMePreferenceField from "./BookForMePreferenceField";

interface BookForMeFormProps {
  form: UseFormReturn<BookForMeFormData>;
  onSubmit: (data: BookForMeFormData) => void;
  isSubmitting?: boolean;
}

export const BookForMeForm = ({
  form,
  onSubmit,
  isSubmitting = false,
}: BookForMeFormProps) => {
  const { control, handleSubmit } = form;

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Card className="space-y-7 rounded-3xl border-none bg-white p-4 shadow-product-card">
        {/* 1. Trip details */}
        <section className="space-y-4">
          <StepHeader
            step={1}
            title="Trip details"
            subtitle="Where and when are you going?"
          />

          <div className="space-y-4 pt-1">
            <BookForMeDestinationField control={control} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormDateRangeField
                name="date"
                control={control}
                label="Date"
                placeholder="Select check-in & check-out date"
                leadingIcon={<IconCalendar className="size-5" />}
              />

              <FormTravelerField
                name="guests"
                control={control}
                label="Guests"
                placeholder="2 adults · 1 child · 1 room"
                leadingIcon={<IconUsers className="size-5" />}
              />
            </div>
          </div>
        </section>

        {/* 2. Budget (optional) */}
        <section className="space-y-4">
          <StepHeader
            step={2}
            title="Budget (optional)"
            subtitle="Let us know your preferred budget range per night"
          />

          <div className="grid grid-cols-1 gap-4 pt-1 sm:grid-cols-2">
            <FormInputField
              name="budgetMin"
              control={control}
              label="Min"
              placeholder="Enter minimum"
              leadingIcon="$"
            />

            <FormInputField
              name="budgetMax"
              control={control}
              label="Max"
              placeholder="Enter maximum"
              leadingIcon="$"
            />
          </div>
        </section>

        {/* 3. Your preferences (optional) */}
        <section className="space-y-4">
          <StepHeader
            step={3}
            title="Your preferences (optional)"
            subtitle="Select what matters to you. You can choose multiple options."
          />

          <BookForMePreferenceField control={control} />
        </section>

        {/* 4. Additional requests (optional) */}
        <section className="space-y-4">
          <StepHeader
            step={4}
            title="Additional requests (optional)"
            subtitle="Tell us anything else, such as hotel type, room setup, special occasions, or other preferences."
          />

          <div className="pt-1">
            <FormInputField
              name="additionalRequests"
              control={control}
              label="Additional requests (optional)"
              placeholder="tell us anything else (e.g, special requests, occasion, room type)"
            />
          </div>
        </section>

        {/* Submit Button */}
        <div className="pt-2">
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            size="md"
            className="w-full"
          >
            Send enquiry
          </Button>
        </div>
      </Card>
    </form>
  );
};

export default memo(BookForMeForm);
